/**
 * Dashboard API service
 * Centralized API client with standardized methods and error handling
 */

import { computeEmailsCampaignId } from "../utils/helpers";
import type {
  ApiResponse,
  ProspectRow,
  ProspectListResult,
  DatabaseQueryResult,
  DatabaseConfig,
  ProgressCallback,
  DashboardData,
  UtmLinkResponse,
  MediaVaultListResponse,
  MediaVaultItemResponse,
  MediaVaultDeleteResponse,
  DocumentTreeResponse,
  DocumentFileResponse,
  DocumentFolderResponse,
  DocumentFile,
  UnifiedConversationResult,
  ConversationReplyResult,
  McpRemoveResponse,
  ConnectorManagerResponse,
  TwitterAdsResponse,
  AdPlatformAccessResponse,
  AdPlatformAccessListResponse,
  AdvertiserVerificationStatusResponse,
  AdvertiserVerificationUpdateResponse,
} from "../types/api";
import type {
  TenantResolveResponse,
  TenantCampaignsResponse,
  CreateCampaignPayload,
  CreateCampaignResponse,
  CampaignStatusUpdateResponse,
  CampaignResolveResponse,
  EmailTenantResolveResponse,
  WizardBriefFields,
  WizardScrapeResponse,
  WizardRegenerateRequest,
  WizardRegenerateResponse,
  TenantPlanResponse,
  AllPlansResponse,
  PlanChangeResponse,
} from "../types/tenant";
import type { SocialListeningDashboardData } from "../types/socialListening";

// =============================================================================
// API Configuration
// =============================================================================

let API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

try {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const isLocal =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("10.") ||
      hostname.startsWith("192.168.") ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(hostname);

    if (isLocal && hostname !== "localhost" && hostname !== "127.0.0.1") {
      // Accessing from LAN IP — point API to the same host on port 3001
      API_BASE_URL = `http://${hostname}:3001/api`;
    }

    if (!isLocal) {
      const envUrl = process.env.REACT_APP_API_URL || "";
      const looksLocal = /localhost|127\.0\.0\.1/i.test(envUrl);

      if (!envUrl || looksLocal) {
        // Fail loudly in production — a missing env var causes every API call to
        // silently target localhost:3001, which never resolves in the browser.
        const msg = "REACT_APP_API_URL is not configured — set it in the Amplify Console environment variables";
        if (process.env.NODE_ENV === "production") {
          throw new Error(msg);
        }
        console.warn(msg);
      } else {
        API_BASE_URL = envUrl;
      }
    }
  }
} catch {
  // noop - use defaults if window not available
}

// Ensure base URL ends with /api (handles env var configured without it)
if (API_BASE_URL && !API_BASE_URL.endsWith("/api")) {
  API_BASE_URL = API_BASE_URL.replace(/\/+$/, "") + "/api";
}

// =============================================================================
// Custom Error Type
// =============================================================================

interface ApiRequestError extends Error {
  status?: number;
  response?: Response;
}

// =============================================================================
// API Service Class
// =============================================================================

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: string | Record<string, unknown>;
}

interface AdsRoundsParams {
  status?: string;
  platform?: string;
  limit?: number;
  search?: string;
  campaign?: string;
  group?: boolean;
}

export interface MonitoringEventInput {
  event_type: string;
  category?: string;
  severity?: string;
  payload?: Record<string, unknown>;
  user_id?: string;
  tenant_id?: string;
  session_id?: string;
  page_url?: string;
  created_at?: string;
}

export interface MonitoringEvent {
  id: number;
  event_type: string;
  category: string;
  severity: string;
  payload: Record<string, unknown>;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  tenant_id: string | null;
  session_id: string | null;
  page_url: string | null;
  created_at: string;
}

export interface MonitoringUser {
  user_id: string;
  user_name: string | null;
  user_email: string | null;
}

export interface MonitoringQueryParams {
  type?: string;
  severity?: string;
  from?: string;
  to?: string;
  search?: string;
  user_id?: string;
  domain?: string;
  page?: number;
  limit?: number;
}

export interface MonitoringEventsResult {
  success: boolean;
  rows: MonitoringEvent[];
  total: number;
  page: number;
  limit: number;
  users?: MonitoringUser[];
  domains?: string[];
}

export interface MonitoringAggregateRow {
  bucket: string;
  event_type: string;
  severity: string;
  count: string;
}

export interface MonitoringAggregateResult {
  success: boolean;
  rows: MonitoringAggregateRow[];
  aggregate: string;
}

export interface MonitoringTopPage {
  page_path: string;
  view_count: string;
  avg_load_time_ms: string | null;
  avg_dom_interactive_ms: string | null;
}

export interface MonitoringPageViewSeries {
  bucket: string;
  page_views: string;
  avg_load_time_ms: string | null;
}

export interface MonitoringMetricsResult {
  success: boolean;
  metrics: {
    top_pages: MonitoringTopPage[];
    error_rate: {
      total_events: number;
      error_count: number;
      rate_percent: number;
    };
    api_latency: {
      avg_ms: number;
      p50_ms: number;
      p95_ms: number;
      p99_ms: number;
      total_requests: number;
      failed_requests: number;
    };
    req_per_sec: number;
    page_view_series: MonitoringPageViewSeries[];
  };
}

class ApiService {
  baseURL: string;
  private authToken: string | null = null;
  private tokenRefresher: (() => Promise<string | null>) | null = null;
  tenantId: string | null = null;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  hasAuthToken(): boolean {
    return Boolean(this.authToken);
  }

  /** Register a Clerk getToken() callback so request() can auto-retry on 401. */
  setTokenRefresher(fn: () => Promise<string | null>): void {
    this.tokenRefresher = fn;
  }

  setTenantId(id: string | null): void {
    this.tenantId = id;
  }

  // ---------------------------------------------------------------------------
  // Core Request Methods
  // ---------------------------------------------------------------------------

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const { body, headers, ...restOptions } = options;
    const method = options.method || "GET";

    // Skip tracking for event ingestion endpoints to avoid infinite loop
    const shouldTrack = !endpoint.startsWith("/events");
    const startTime = shouldTrack ? performance.now() : 0;

    // Include session auth header if authenticated
    const authToken = typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("mainPageAuth") || ""
      : "";

    const config: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
        ...(authToken ? { "x-session-auth": authToken } : {}),
        ...(this.tenantId ? { "x-tenant-id": this.tenantId } : {}),
        ...(headers as Record<string, string>),
      },
      ...restOptions,
    };

    if (body) {
      config.body = typeof body === "string" ? body : JSON.stringify(body);
    }

    // Retry config: only GET requests, only on 5xx or network errors
    // GET: up to 2 retries on 5xx/network errors.
    // POST/etc: 1 extra attempt reserved exclusively for 401 token-refresh retry.
    const maxRetries = method === "GET" ? 2 : 1;
    let lastError: Error | null = null;
    let totalAttempts = 0;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      totalAttempts = attempt + 1;
      try {
        const response = await fetch(url, config);

        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorBody = await response.json();
            errorMessage = errorBody.error || errorBody.message || errorMessage;
          } catch {
            try {
              const errorText = await response.text();
              if (errorText) errorMessage = errorText;
            } catch {
              // Ignore text reading errors
            }
          }
          const error: ApiRequestError = new Error(errorMessage);
          error.status = response.status;
          error.response = response;

          // On 401: try refreshing the token once and retry the request
          if (response.status === 401 && this.tokenRefresher && attempt === 0) {
            try {
              const freshToken = await this.tokenRefresher();
              if (freshToken) {
                this.authToken = freshToken;
                // Rebuild config with new token and retry immediately
                config.headers = {
                  ...config.headers as Record<string, string>,
                  Authorization: `Bearer ${freshToken}`,
                };
                continue;
              }
            } catch {
              // Refresher failed — fall through to throw the original 401
            }
          }

          // Only retry on 5xx (server errors), not 4xx (client errors)
          if (response.status >= 500 && attempt < maxRetries) {
            lastError = error;
            await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
            continue;
          }

          // Track failed request
          if (shouldTrack) {
            this.trackApiRequest(endpoint, method, response.status, performance.now() - startTime, totalAttempts, errorMessage);
          }

          throw error;
        }

        // Track successful request
        if (shouldTrack) {
          this.trackApiRequest(endpoint, method, response.status, performance.now() - startTime, totalAttempts, null);
        }

        return (await response.json()) as T;
      } catch (error) {
        lastError = error as Error;

        // Retry on network errors (no response at all) for GET requests
        const isNetworkError = !(error as ApiRequestError).status;
        if (isNetworkError && attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }

        // Don't retry 4xx or if we've exhausted retries
        if (attempt >= maxRetries) {
          console.error(`API request failed for ${endpoint} after ${attempt + 1} attempt(s):`, error);

          // Track network error
          if (shouldTrack) {
            this.trackApiRequest(endpoint, method, 0, performance.now() - startTime, totalAttempts, (error as Error).message);
          }

          throw error;
        }
      }
    }

    // Should never reach here, but TypeScript needs it
    throw lastError || new Error(`Request failed for ${endpoint}`);
  }

  private trackApiRequest(
    endpoint: string,
    method: string,
    status: number,
    latencyMs: number,
    attempts: number,
    error: string | null
  ): void {
    // Lazy import to avoid circular dependency at module load time
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { eventTracker } = require("services/eventTracker");
      eventTracker.track("api_request", {
        endpoint,
        method,
        status,
        latency_ms: Math.round(latencyMs),
        attempts,
        error,
      }, {
        category: "api",
        severity: error ? "error" : "info",
      });
    } catch {
      // eventTracker not available yet — skip silently
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  async post<T>(endpoint: string, data: Record<string, unknown>): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: Record<string, unknown>): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  async getAdPlatformAccessAll(tenantId: string): Promise<AdPlatformAccessListResponse> {
    return this.get<AdPlatformAccessListResponse>(`/tenants/${tenantId}/ad-platform-access`);
  }

  async confirmAdPlatformAccess(tenantId: string, platform: string = "x_ads"): Promise<AdPlatformAccessResponse> {
    return this.post<AdPlatformAccessResponse>(`/tenants/${tenantId}/ad-platform-access/confirm`, { platform });
  }

  async getAdvertiserVerificationStatus(
    tenantId: string,
    campaignKey: string,
  ): Promise<AdvertiserVerificationStatusResponse> {
    return this.get<AdvertiserVerificationStatusResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/advertiser-verification/${encodeURIComponent(campaignKey)}`,
    );
  }

  async getPublicAdvertiserVerificationStatus(
    campaignKey: string,
  ): Promise<AdvertiserVerificationStatusResponse> {
    return this.get<AdvertiserVerificationStatusResponse>(
      `/public/advertiser-verification/${encodeURIComponent(campaignKey)}`,
    );
  }

  async updateAdvertiserVerificationStatus(
    tenantId: string,
    campaignKey: string,
    data: {
      platform: string;
      approved: boolean;
      completedSteps: Record<string, boolean>;
    },
  ): Promise<AdvertiserVerificationUpdateResponse> {
    return this.request<AdvertiserVerificationUpdateResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/advertiser-verification/${encodeURIComponent(campaignKey)}`,
      {
        method: "PATCH",
        body: data,
      },
    );
  }

  async getAdsRoundsOverview(campaign?: string) {
    const sp = new URLSearchParams();
    if (campaign) sp.set("campaign", campaign);
    const q = sp.toString();
    return this.request(`/ads/rounds/overview${q ? `?${q}` : ""}`);
  }

  async getAdsRounds(params: AdsRoundsParams = {}): Promise<Record<string, unknown>[]> {
    const sp = new URLSearchParams();
    if (params.status) sp.set("status", params.status);
    if (params.platform) sp.set("platform", params.platform);
    if (params.limit != null) sp.set("limit", String(params.limit));
    if (params.search) sp.set("search", params.search);
    if (params.campaign) sp.set("campaign", params.campaign);
    if (params.group === false) sp.set("group", "false");
    const q = sp.toString();
    return this.request(`/ads/rounds${q ? `?${q}` : ""}`);
  }

  async getCustomerAdRounds(campaign: string, days?: number): Promise<Record<string, unknown>[]> {
    const d = days ? `&days=${days}` : "";
    return this.request(`/ads/customer/rounds?campaign=${encodeURIComponent(campaign)}${d}`);
  }

  async getCustomerAdOverview(campaign: string, days?: number): Promise<Record<string, unknown>> {
    const d = days ? `&days=${days}` : "";
    return this.request(`/ads/customer/overview?campaign=${encodeURIComponent(campaign)}${d}`);
  }

  async getCustomerAdDaily(campaign: string, days?: number): Promise<Record<string, unknown>[]> {
    const d = days ? `&days=${days}` : "";
    return this.request(`/ads/customer/daily?campaign=${encodeURIComponent(campaign)}${d}`);
  }

  async getCustomerAds(campaign: string): Promise<Record<string, unknown>[]> {
    return this.request(`/ads/customer/ads?campaign=${encodeURIComponent(campaign)}`);
  }

  async patchAdsRound(runId: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request(`/ads/rounds/${encodeURIComponent(runId)}`, {
      method: "PATCH",
      body,
    });
  }

  // ---------------------------------------------------------------------------
  // Database Query Methods
  // ---------------------------------------------------------------------------

  async executeDatabaseQuery(
    query: string,
  ): Promise<DatabaseQueryResult> {
    const body: Record<string, unknown> = { query };
    return this.request<DatabaseQueryResult>("/database/query", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  // ---------------------------------------------------------------------------
  // Content Methods (GeneratedContent page)
  // ---------------------------------------------------------------------------

  async getContentData(campaign: string): Promise<{
    success: boolean;
    sections: Record<string, Record<string, unknown>[]>;
    campaignTitle: string;
    error?: string;
  }> {
    return this.get(`/content/data?campaign=${encodeURIComponent(campaign)}`);
  }

  async getEmailSequenceData(campaignId: string): Promise<{
    success: boolean;
    rounds: Record<string, unknown>[];
    steps: Record<string, unknown>[];
    variants: Record<string, unknown>[];
    error?: string;
  }> {
    return this.get(`/content/email-sequence?campaign_id=${encodeURIComponent(campaignId)}`);
  }

  async updateKickoff(
    campaignId: string,
    fields: Record<string, unknown>,
  ): Promise<{ success: boolean; row?: Record<string, unknown>; error?: string }> {
    return this.request("/content/kickoff", {
      method: "PATCH",
      body: { campaign_id: campaignId, fields },
    });
  }

  async updateContent(params: {
    schema: string;
    table: string;
    column: string;
    primaryKey: string;
    primaryKeyValue: unknown;
    value: unknown;
    valueType?: string;
  }): Promise<{ success: boolean; rowCount?: number; error?: string }> {
    return this.put("/content/update", params as unknown as Record<string, unknown>);
  }

  async getAdsMedia(campaign: string): Promise<{
    success: boolean;
    campaign: string;
    images: Array<{
      id: string;
      stage: number;
      urls: string[];
      created_at: string;
    }>;
    error?: string;
  }> {
    return this.get(`/content/ads-media?campaign=${encodeURIComponent(campaign)}`);
  }

  async getActiveCampaigns(): Promise<DatabaseQueryResult> {
    return this.get<DatabaseQueryResult>("/campaigns/active");
  }

  async getCampaignInfo(campaignId: string): Promise<DatabaseQueryResult> {
    return this.get<DatabaseQueryResult>(`/campaigns/info?campaign_id=${encodeURIComponent(campaignId)}`);
  }

  async getBulkCampaignMetrics(): Promise<{
    success: boolean;
    metrics: Record<string, {
      prospects: number;
      accountsConnected: number;
      messagesSent: number;
      emails: number;
      adImpressions: number;
      clicks: number;
      lastLambdaEvent: {
        last_event_time?: string;
        lastEventTime?: string;
        action: string;
        status: string;
      } | null;
    }>;
    error?: string;
  }> {
    return this.get("/campaigns/metrics/bulk");
  }

  async getMessagingSections(): Promise<{
    success: boolean;
    sections: {
      kickoff: Record<string, unknown>[];
      messaging: Record<string, unknown>[];
      x_ads: Record<string, unknown>[];
      google_ads: Record<string, unknown>[];
    };
    error?: string;
  }> {
    return this.get("/campaigns/messaging-sections");
  }

  // ---------------------------------------------------------------------------
  // Prospects Methods
  // ---------------------------------------------------------------------------

  async getCombinedProspectList(
    campaignId: string,
    maxRows: number = 2000,
    onProgress: ProgressCallback | null = null,
  ): Promise<ProspectListResult> {
    if (!campaignId || campaignId === "null" || campaignId === "undefined") {
      return { success: true, rows: [], columns: [], rowCount: 0, totalRows: 0, campaignId: campaignId || "" };
    }
    try {
      if (onProgress) {
        onProgress({ current: 0, total: maxRows, percentage: 0 });
      }

      let resp: ApiResponse<ProspectRow> | null;
      try {
        resp = await this.request<ApiResponse<ProspectRow>>(
          `/prospects/database?campaign_id=${encodeURIComponent(campaignId)}&limit=${maxRows}&offset=0`,
        );
      } catch (primaryErr) {
        const errMsg = primaryErr instanceof Error ? primaryErr.message : String(primaryErr);
        console.error("Primary combined fetch failed, attempting fallback merge:", errMsg);
        resp = null;
      }

      if (onProgress && resp) {
        const total = Number(resp.totalRows || resp.rowCount || 0);
        const current = Math.min(total, maxRows);
        onProgress({
          current,
          total: maxRows,
          percentage: Math.min(100, Math.round((current / maxRows) * 100)),
        });
      }

      const respRows = Array.isArray(resp?.rows) ? resp!.rows : [];
      const respTotal = Number(resp?.totalRows ?? 0);

      if (respRows.length > 0 || respTotal > 0) {
        return {
          success: true,
          rows: respRows,
          columns: resp?.columns || [],
          rowCount: resp?.rowCount ?? respRows.length,
          totalRows: respTotal || respRows.length,
          hasMore: !!resp?.hasMore,
          query: resp?.query,
          campaignId,
        };
      }

      // For __all__, backend already aggregates all campaigns — skip fallback merge
      if (campaignId === "__all__") {
        return {
          success: true,
          rows: [],
          columns: [],
          rowCount: 0,
          totalRows: 0,
          hasMore: false,
          query: "",
          campaignId,
        };
      }

      const emailsCampaignId = computeEmailsCampaignId(campaignId);
      const limit = maxRows;

      const [stdRes, emlRes] = await Promise.all([
        this.request<ApiResponse<ProspectRow>>(
          `/prospects/database?campaign_id=${encodeURIComponent(campaignId)}&limit=${limit}&offset=0`,
        ),
        this.request<ApiResponse<ProspectRow>>(
          `/prospects/database?campaign_id=${encodeURIComponent(emailsCampaignId!)}&limit=${limit}&offset=0`,
        ),
      ]);

      const stdRows = Array.isArray(stdRes?.rows) ? stdRes.rows : [];
      const emlRows = Array.isArray(emlRes?.rows) ? emlRes.rows : [];

      const seen = new Set<string | number>();
      const merged: ProspectRow[] = [];
      const pushUnique = (row: ProspectRow) => {
        const key = row?.id ?? `${row?.user_id || ""}|${row?.emails || ""}`;
        if (!seen.has(key as string | number)) {
          seen.add(key as string | number);
          merged.push(row);
        }
      };
      stdRows.forEach(pushUnique);
      emlRows.forEach(pushUnique);

      return {
        success: true,
        rows: merged,
        columns: stdRes?.columns || emlRes?.columns || [],
        rowCount: merged.length,
        totalRows: merged.length,
        hasMore: false,
        query: `FALLBACK_COMBINED(${campaignId}+${emailsCampaignId})`,
        campaignId,
      };
    } catch (e) {
      console.error("Combined prospects fetch failed for", campaignId, e);
      return {
        success: true,
        rows: [],
        columns: [],
        rowCount: 0,
        totalRows: 0,
        hasMore: false,
        query: "",
        campaignId,
      };
    }
  }

  async getProspectListFromDatabase(
    campaignId: string | null = null,
    onProgress: ProgressCallback | null = null,
  ): Promise<ProspectListResult> {
    if (!campaignId || campaignId === "null" || campaignId === "undefined") {
      return { success: true, rows: [], columns: [], rowCount: 0, totalRows: 0, campaignId: campaignId || "" };
    }
    const batchSize = 2000;
    let allRows: ProspectRow[] = [];
    let offset = 0;
    let hasMore = true;
    let totalRows = 0;

    console.log(`Starting paginated prospect fetch for campaign: ${campaignId}`);

    try {
      const firstBatch = await this.request<ApiResponse<ProspectRow>>(
        `/prospects/database?campaign_id=${campaignId}&limit=${batchSize}&offset=0`,
      );

      if (!firstBatch.success) {
        return firstBatch as unknown as ProspectListResult;
      }

      totalRows = firstBatch.totalRows || firstBatch.rows?.length || 0;
      allRows = firstBatch.rows || [];
      hasMore = !!firstBatch.hasMore;
      offset = batchSize;

      console.log(`First batch: ${allRows.length} of ${totalRows} total rows`);

      if (onProgress) {
        onProgress({
          current: allRows.length,
          total: totalRows,
          percentage: Math.round((allRows.length / totalRows) * 100),
        });
      }

      let batchNumber = 2;
      while (hasMore && offset < totalRows) {
        console.log(`Fetching batch ${batchNumber}: offset ${offset}, limit ${batchSize}`);

        const batch = await this.request<ApiResponse<ProspectRow>>(
          `/prospects/database?campaign_id=${campaignId}&limit=${batchSize}&offset=${offset}`,
        );

        if (batch.success && batch.rows && batch.rows.length > 0) {
          allRows = [...allRows, ...batch.rows];
          hasMore = !!batch.hasMore;
          offset += batchSize;
          batchNumber++;

          console.log(`Batch ${batchNumber - 1} complete: ${allRows.length} of ${totalRows} total rows`);

          if (onProgress) {
            onProgress({
              current: allRows.length,
              total: totalRows,
              percentage: Math.round((allRows.length / totalRows) * 100),
            });
          }
        } else {
          break;
        }
      }

      console.log(`All batches complete: ${allRows.length} total rows fetched`);

      return {
        success: true,
        rows: allRows,
        columns: firstBatch.columns || [],
        rowCount: allRows.length,
        totalRows: totalRows,
        query: firstBatch.query,
        campaignId: campaignId,
      };
    } catch (error) {
      console.error("Paginated fetch error:", error);
      throw error;
    }
  }

  async getProspectListFromDatabaseWithLimit(
    campaignId: string | null = null,
    maxRows: number = 5000,
    onProgress: ProgressCallback | null = null,
  ): Promise<ProspectListResult> {
    if (!campaignId || campaignId === "null" || campaignId === "undefined") {
      return { success: true, rows: [], columns: [], rowCount: 0, totalRows: 0, campaignId: campaignId || "" };
    }
    const batchSize = 2000;
    let allRows: ProspectRow[] = [];
    let offset = 0;
    let totalRows = 0;

    console.log(`Starting limited prospect fetch for campaign: ${campaignId} (max: ${maxRows} rows)`);

    try {
      const firstBatch = await this.request<ApiResponse<ProspectRow>>(
        `/prospects/database?campaign_id=${campaignId}&limit=${Math.min(batchSize, maxRows)}&offset=0`,
      );

      if (!firstBatch.success) {
        return firstBatch as unknown as ProspectListResult;
      }

      totalRows = firstBatch.totalRows || firstBatch.rows?.length || 0;
      allRows = firstBatch.rows || [];
      offset = Math.min(batchSize, maxRows);

      console.log(`First batch: ${allRows.length} of ${Math.min(totalRows, maxRows)} rows (total in DB: ${totalRows})`);

      if (onProgress) {
        onProgress({
          current: allRows.length,
          total: Math.min(totalRows, maxRows),
          percentage: Math.round((allRows.length / Math.min(totalRows, maxRows)) * 100),
        });
      }

      let batchNumber = 2;
      while (allRows.length < maxRows && allRows.length < totalRows) {
        const remainingRows = Math.min(maxRows - allRows.length, batchSize);
        console.log(`Fetching batch ${batchNumber}: offset ${offset}, limit ${remainingRows}`);

        const batch = await this.request<ApiResponse<ProspectRow>>(
          `/prospects/database?campaign_id=${campaignId}&limit=${remainingRows}&offset=${offset}`,
        );

        if (batch.success && batch.rows && batch.rows.length > 0) {
          allRows = [...allRows, ...batch.rows];
          offset += remainingRows;
          batchNumber++;

          console.log(`Batch ${batchNumber - 1} complete: ${allRows.length} of ${Math.min(totalRows, maxRows)} rows`);

          if (onProgress) {
            onProgress({
              current: allRows.length,
              total: Math.min(totalRows, maxRows),
              percentage: Math.round((allRows.length / Math.min(totalRows, maxRows)) * 100),
            });
          }
        } else {
          break;
        }
      }

      console.log(`Limited fetch complete: ${allRows.length} rows fetched (${totalRows} total available)`);

      return {
        success: true,
        rows: allRows,
        columns: firstBatch.columns || [],
        rowCount: allRows.length,
        totalRows: totalRows,
        loadedRows: allRows.length,
        hasMore: allRows.length < totalRows,
        query: firstBatch.query,
        campaignId: campaignId,
      };
    } catch (error) {
      console.error("Limited fetch error:", error);
      throw error;
    }
  }

  async loadMoreProspects(
    campaignId: string | null = null,
    currentCount: number = 0,
    loadCount: number = 5000,
    onProgress: ProgressCallback | null = null,
  ): Promise<ProspectListResult> {
    console.log(`Loading ${loadCount} more prospects for campaign: ${campaignId} (current: ${currentCount})`);

    try {
      const result = await this.request<ApiResponse<ProspectRow>>(
        `/prospects/database?campaign_id=${campaignId}&limit=${loadCount}&offset=${currentCount}`,
      );

      if (!result.success) {
        return result as unknown as ProspectListResult;
      }

      console.log(`Load more complete: ${result.rows?.length || 0} additional rows loaded`);

      if (onProgress) {
        onProgress({
          current: result.rows?.length || 0,
          total: loadCount,
          percentage: Math.round(((result.rows?.length || 0) / loadCount) * 100),
        });
      }

      return {
        success: true,
        rows: result.rows || [],
        columns: result.columns || [],
        rowCount: result.rows?.length || 0,
        totalRows: result.totalRows || 0,
        hasMore: result.hasMore,
        query: result.query,
        campaignId: campaignId,
      };
    } catch (error) {
      console.error("Load more prospects error:", error);
      throw error;
    }
  }

  // ---------------------------------------------------------------------------
  // Metrics Methods
  // ---------------------------------------------------------------------------

  async getMetrics(): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>("/metrics");
  }

  async getGrowthMetrics(): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>("/metrics/growth");
  }

  async getAdMetrics(): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>("/metrics/ads");
  }

  async getAdsRoundByRunId(runId: string): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>(`/ads/rounds/${encodeURIComponent(runId)}`);
  }

  async getAdsRoundPerformance(
    runId: string
  ): Promise<Array<{ date: string; ctr: number | null; cpc: number | null; impressions: number; clicks: number }>> {
    return this.request(`/ads/rounds/${encodeURIComponent(runId)}/performance`);
  }

  async getProspects(): Promise<ApiResponse<ProspectRow>> {
    return this.request<ApiResponse<ProspectRow>>("/prospects");
  }

  async getProspectsCount(): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>("/prospects/count");
  }

  // ---------------------------------------------------------------------------
  // Webinar Methods
  // ---------------------------------------------------------------------------

  async getWebinarAttendees(campaignId: string | null = null): Promise<ApiResponse<Record<string, unknown>>> {
    if (!campaignId) return { success: true, rows: [], columns: [], rowCount: 0 } as ApiResponse<Record<string, unknown>>;
    return this.request<ApiResponse<Record<string, unknown>>>(`/webinar/attendees?campaign_id=${campaignId}`);
  }

  // ---------------------------------------------------------------------------
  // Outreach Methods
  // ---------------------------------------------------------------------------

  async getConnectionRequests(campaignName: string | null = null): Promise<ApiResponse<Record<string, unknown>>> {
    if (!campaignName) return { success: true, rows: [], columns: [], rowCount: 0 } as ApiResponse<Record<string, unknown>>;
    return this.request<ApiResponse<Record<string, unknown>>>(`/connection-requests?campaign=${campaignName}`);
  }

  async getMessagesSent(campaignName: string | null = null): Promise<ApiResponse<Record<string, unknown>>> {
    if (!campaignName) return { success: true, rows: [], columns: [], rowCount: 0 } as ApiResponse<Record<string, unknown>>;
    return this.request<ApiResponse<Record<string, unknown>>>(`/outreach-messages?campaign=${campaignName}`);
  }

  async getMessagesResponses(campaignName: string | null = null): Promise<ApiResponse<Record<string, unknown>>> {
    if (!campaignName) return { success: true, rows: [], columns: [], rowCount: 0 } as ApiResponse<Record<string, unknown>>;
    return this.request<ApiResponse<Record<string, unknown>>>(`/responses-received?campaign=${campaignName}`);
  }

  async getNodeNicknames(): Promise<ApiResponse<{ nickname: string }>> {
    return this.request<ApiResponse<{ nickname: string }>>("/nodes/nicknames");
  }

  async getUnifiedConversations(
    campaignName: string | null = null,
    cursor?: string | null,
    limit: number = 200,
  ): Promise<UnifiedConversationResult> {
    if (!campaignName) return { success: true, rows: [], next_cursor: null, rowCount: 0 };
    const params = new URLSearchParams({ campaign: campaignName, limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    return this.request<UnifiedConversationResult>(`/conversations/unified?${params.toString()}`);
  }

  async getSocialListeningData(campaign: string): Promise<SocialListeningDashboardData> {
    const params = new URLSearchParams({ campaign });
    return this.get<SocialListeningDashboardData>(`/social-listening?${params.toString()}`);
  }

  async getPublicSocialListeningData(campaign: string): Promise<SocialListeningDashboardData> {
    return this.get<SocialListeningDashboardData>(
      `/public/social-listening/${encodeURIComponent(campaign)}`,
    );
  }

  // Queue a customer-authored reply for the outreach agent to send on its next run.
  async sendConversationReply(
    campaign: string,
    userId: string,
    message: string,
    service: string = "linkedin",
  ): Promise<ConversationReplyResult> {
    return this.post<ConversationReplyResult>("/conversations/reply", {
      campaign,
      userId,
      service,
      message,
    });
  }

  // ---------------------------------------------------------------------------
  // Advertising Methods
  // ---------------------------------------------------------------------------

  async getAdvertisingPerformance(campaignName: string | null = null): Promise<ApiResponse<Record<string, unknown>>> {
    if (!campaignName) return { success: true, rows: [], columns: [], rowCount: 0 } as ApiResponse<Record<string, unknown>>;
    return this.request<ApiResponse<Record<string, unknown>>>(`/advertising/performance?campaign_name=${campaignName}`);
  }

  async getTwitterAds(campaign: string): Promise<TwitterAdsResponse> {
    const params = new URLSearchParams({ campaign });
    return this.get<TwitterAdsResponse>(`/content/twitter-ads?${params}`);
  }

  // ---------------------------------------------------------------------------
  // UTM Link Methods
  // ---------------------------------------------------------------------------

  async getUtmLink(
    campaignId: string,
    options: { source?: string; medium?: string; content?: string; term?: string } = {},
  ): Promise<UtmLinkResponse> {
    const params = new URLSearchParams({ campaignId });
    if (options.source) params.append('source', options.source);
    if (options.medium) params.append('medium', options.medium);
    if (options.content) params.append('content', options.content);
    if (options.term) params.append('term', options.term);
    return this.request<UtmLinkResponse>(`/utm-link?${params.toString()}`);
  }

  // ---------------------------------------------------------------------------
  // Email Methods
  // ---------------------------------------------------------------------------

  async getEmailMetrics(campaignName: string | null = null): Promise<ApiResponse<Record<string, unknown>>> {
    if (!campaignName) return { success: true, rows: [], columns: [], rowCount: 0 } as ApiResponse<Record<string, unknown>>;
    return this.request<ApiResponse<Record<string, unknown>>>(`/email-metrics?campaign_name=${campaignName}`);
  }

  async getEmailRecipients(campaignName: string): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<ApiResponse<Record<string, unknown>>>(`/email-recipients?campaign_name=${encodeURIComponent(campaignName)}`);
  }

  // ---------------------------------------------------------------------------
  // Dashboard Data Methods
  // ---------------------------------------------------------------------------

  async getDashboardData(): Promise<DashboardData> {
    try {
      const metrics = await this.getMetrics();
      return {
        metrics,
        prospects: [],
        webinarAttendees: [],
        connectionRequests: [],
        messagesSent: [],
        messagesResponses: [],
      };
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      throw error;
    }
  }

  // ---------------------------------------------------------------------------
  // Document Storage Methods
  // ---------------------------------------------------------------------------

  async getDocumentTree(tenantId: string, folderId?: string): Promise<DocumentTreeResponse> {
    const query = folderId ? `?folder_id=${encodeURIComponent(folderId)}` : '';
    return this.request<DocumentTreeResponse>(`/tenants/${encodeURIComponent(tenantId)}/documents/tree${query}`);
  }

  async createDocumentFolder(tenantId: string, data: { name: string; parent_id?: string | null }): Promise<DocumentFolderResponse> {
    return this.post<DocumentFolderResponse>(`/tenants/${encodeURIComponent(tenantId)}/documents/folders`, data);
  }

  async updateDocumentFolder(tenantId: string, folderId: string, name: string): Promise<DocumentFolderResponse> {
    return this.request<DocumentFolderResponse>(`/tenants/${encodeURIComponent(tenantId)}/documents/folders/${encodeURIComponent(folderId)}`, {
      method: 'PATCH',
      body: { name },
    });
  }

  async moveDocumentFolder(tenantId: string, folderId: string, parent_id: string | null): Promise<DocumentFolderResponse> {
    return this.post<DocumentFolderResponse>(`/tenants/${encodeURIComponent(tenantId)}/documents/folders/${encodeURIComponent(folderId)}/move`, { parent_id });
  }

  async deleteDocumentFolder(tenantId: string, folderId: string): Promise<{ success: boolean; deleted_id: string; deleted_count: number }> {
    return this.delete(`/tenants/${encodeURIComponent(tenantId)}/documents/folders/${encodeURIComponent(folderId)}`);
  }

  async moveDocumentFile(tenantId: string, fileId: string, parent_id: string | null): Promise<DocumentFileResponse> {
    return this.post<DocumentFileResponse>(`/tenants/${encodeURIComponent(tenantId)}/documents/files/${encodeURIComponent(fileId)}/move`, { parent_id });
  }

  async replaceDocumentFile(tenantId: string, file: File, fileId: string): Promise<DocumentFileResponse> {
    const response = await fetch(`${this.baseURL}/tenants/${encodeURIComponent(tenantId)}/documents/files/${encodeURIComponent(fileId)}/replace`, {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-tenant-id': tenantId,
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || `Document replacement failed: ${response.status}`);
    }
    return response.json();
  }

  async createTextDocument(
    tenantId: string,
    data: { name: string; content?: string; parent_id?: string | null },
  ): Promise<DocumentFileResponse> {
    return this.post<DocumentFileResponse>(`/tenants/${encodeURIComponent(tenantId)}/documents/files/text`, data);
  }

  async uploadDocumentFile(tenantId: string, file: File, parentId?: string | null): Promise<DocumentFileResponse> {
    const response = await fetch(`${this.baseURL}/tenants/${encodeURIComponent(tenantId)}/documents/files/upload`, {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-filename': file.name,
        'x-tenant-id': tenantId,
        ...(parentId ? { 'x-parent-folder-id': parentId } : {}),
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || `Document upload failed: ${response.status}`);
    }
    return response.json();
  }

  async getDocumentFile(tenantId: string, fileId: string): Promise<DocumentFileResponse> {
    return this.request<DocumentFileResponse>(`/tenants/${encodeURIComponent(tenantId)}/documents/files/${encodeURIComponent(fileId)}`);
  }

  async downloadDocumentFile(tenantId: string, fileId: string): Promise<ArrayBuffer> {
    const response = await fetch(`${this.baseURL}/tenants/${encodeURIComponent(tenantId)}/documents/files/${encodeURIComponent(fileId)}/content`, {
      headers: {
        'x-tenant-id': tenantId,
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || `Document download failed: ${response.status}`);
    }
    return response.arrayBuffer();
  }

  async updateDocumentFile(tenantId: string, fileId: string, data: { name?: string; description?: string }): Promise<DocumentFileResponse> {
    return this.request<DocumentFileResponse>(`/tenants/${encodeURIComponent(tenantId)}/documents/files/${encodeURIComponent(fileId)}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteDocumentFile(tenantId: string, fileId: string): Promise<{ success: boolean; deleted_id: string }> {
    return this.delete(`/tenants/${encodeURIComponent(tenantId)}/documents/files/${encodeURIComponent(fileId)}`);
  }

  async searchDocuments(tenantId: string, search: string): Promise<{ success: boolean; rows: DocumentFile[] }> {
    return this.request(`/tenants/${encodeURIComponent(tenantId)}/documents/search?q=${encodeURIComponent(search)}`);
  }

  // ---------------------------------------------------------------------------
  // Media Vault Methods
  // ---------------------------------------------------------------------------

  async getMediaVaultItems(params: {
    campaign_id?: string;
    type?: string;
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<MediaVaultListResponse> {
    const qs = new URLSearchParams();
    if (params.campaign_id) qs.append('campaign_id', params.campaign_id);
    if (params.type) qs.append('type', params.type);
    if (params.status) qs.append('status', params.status);
    if (params.search) qs.append('search', params.search);
    if (params.limit) qs.append('limit', String(params.limit));
    if (params.offset) qs.append('offset', String(params.offset));
    const query = qs.toString();
    return this.request<MediaVaultListResponse>(`/media-vault${query ? `?${query}` : ''}`);
  }

  async getMediaVaultCounts(campaignId?: string): Promise<{ success: boolean; counts: Record<string, number>; total: number }> {
    const query = campaignId ? `?campaign_id=${campaignId}` : '';
    return this.request(`/media-vault/counts${query}`);
  }

  async getMediaVaultItem(id: number): Promise<MediaVaultItemResponse> {
    return this.request<MediaVaultItemResponse>(`/media-vault/${id}`);
  }

  async getMediaVaultContent(id: number): Promise<string> {
    const response = await fetch(`${this.baseURL}/media-vault/${id}/content`);
    if (!response.ok) {
      throw new Error(`Failed to fetch content: ${response.status}`);
    }
    return response.text();
  }

  async getMediaVaultContentBinary(id: number): Promise<ArrayBuffer> {
    const response = await fetch(`${this.baseURL}/media-vault/${id}/content`);
    if (!response.ok) {
      throw new Error(`Failed to fetch content: ${response.status}`);
    }
    return response.arrayBuffer();
  }

  async uploadMediaVaultFile(file: File, campaignId?: string): Promise<MediaVaultItemResponse> {
    const url = `${this.baseURL}/media-vault/upload`;
    const response = await fetch(url, {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-filename': file.name,
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
        ...(this.tenantId ? { 'x-tenant-id': this.tenantId } : {}),
        ...(campaignId ? { 'x-campaign-id': campaignId } : {}),
      },
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Upload failed: ${response.status}`);
    }
    return response.json();
  }

  async saveMediaVaultUrl(data: {
    campaign_id?: string;
    url: string;
    title: string;
    tags?: string[];
  }): Promise<MediaVaultItemResponse> {
    return this.post<MediaVaultItemResponse>('/media-vault/url', data as Record<string, unknown>);
  }

  async updateMediaVaultItem(id: number, data: {
    title?: string;
    tags?: string[];
    metadata?: Record<string, unknown>;
    status?: string;
  }): Promise<MediaVaultItemResponse> {
    return this.request<MediaVaultItemResponse>(`/media-vault/${id}`, {
      method: 'PATCH',
      body: data as Record<string, unknown>,
    });
  }

  async deleteMediaVaultItem(id: number): Promise<MediaVaultDeleteResponse> {
    return this.delete<MediaVaultDeleteResponse>(`/media-vault/${id}`);
  }

  // ---------------------------------------------------------------------------
  // Developer Dashboard Methods
  // ---------------------------------------------------------------------------

  async getCompletedJobs(): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<ApiResponse<Record<string, unknown>>>("/developer/completed-jobs");
  }

  async getSpawnedJobs(): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<ApiResponse<Record<string, unknown>>>("/developer/spawned-jobs");
  }

  async getNodeJobs(nickname: string): Promise<{
    success: boolean;
    executedJobs: Record<string, unknown>[];
    spawnedJobs: Record<string, unknown>[];
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      executedJobs: Record<string, unknown>[];
      spawnedJobs: Record<string, unknown>[];
      error?: string;
    }>(`/developer/node-jobs?nickname=${encodeURIComponent(nickname)}`);
  }

  async getLambdaJobs(nodeId?: string): Promise<ApiResponse<Record<string, unknown>>> {
    const url = nodeId
      ? `/developer/lambda-jobs?node_id=${encodeURIComponent(nodeId)}`
      : `/developer/lambda-jobs`;
    return this.request<ApiResponse<Record<string, unknown>>>(url);
  }

  // ---------------------------------------------------------------------------
  // Tenant Methods
  // ---------------------------------------------------------------------------

  async resolveTenant(slug: string): Promise<TenantResolveResponse> {
    return this.get<TenantResolveResponse>(
      `/tenants/resolve?slug=${encodeURIComponent(slug)}`,
    );
  }

  async getTenantCampaigns(
    tenantId: string,
    limit: number = 50,
    offset: number = 0,
    status?: string,
  ): Promise<TenantCampaignsResponse> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (status) params.set("status", status);
    return this.get<TenantCampaignsResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/campaigns?${params}`,
    );
  }

  async getTenantPlan(tenantId: string): Promise<TenantPlanResponse> {
    return this.get<TenantPlanResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/plan`,
    );
  }

  async getAllPlans(tenantId: string): Promise<AllPlansResponse> {
    return this.get<AllPlansResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/plans`,
    );
  }

  async requestPlanChange(tenantId: string, planId: number): Promise<PlanChangeResponse> {
    return this.post<PlanChangeResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/plan/change`,
      { planId },
    );
  }

  // ── Onboarding ───────────────────────────────────────────────────────────
  async getOnboardingPlans(): Promise<{ success: boolean; plans: Array<{ id: number; name: string; price: number; features: Array<{ name: string; included: boolean }>; payment_link: string }> }> {
    return this.get(`/onboarding/plans`);
  }

  async getOnboardingStatus(): Promise<{
    success: boolean;
    state: 'plan_selection' | 'awaiting_payment' | 'campaign_creation' | 'complete';
    tenantSlug: string | null;
    tenantId: string | null;
    invoice: { id: number; status: string; paymentLink: string | null; amount: string; docNumber: string | null } | null;
  }> {
    return this.get(`/onboarding/status`);
  }

  async startOnboarding(planId: number, tenantName?: string): Promise<{
    success: boolean;
    tenantId: string;
    tenantSlug: string;
    paymentLink: string;
    invoice: { id: number; status: string; paymentLink: string | null; amount: string; docNumber: string | null } | null;
    error?: string;
  }> {
    return this.post(`/onboarding/start`, { planId, tenantName });
  }

  async uploadLogo(file: File): Promise<{ success: boolean; url?: string; error?: string }> {
    const formData = new FormData();
    formData.append("logo", file);

    const response = await fetch(`${API_BASE_URL}/uploads/logo`, {
      method: "POST",
      body: formData,
      headers: {
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Upload failed (${response.status})`);
    }

    return response.json();
  }

  async getSystemToggles(): Promise<{ success: boolean; toggles: { key: string; is_active: boolean; message: string | null }[] }> {
    return this.get("/system-toggles");
  }

  async getAdminSystemToggles(): Promise<{ success: boolean; toggles: { id: number; key: string; is_active: boolean; message: string | null; toggled_by_email: string | null; toggled_at: string }[] }> {
    return this.get("/admin/system-toggles");
  }

  async updateSystemToggle(key: string, isActive: boolean): Promise<{ success: boolean; toggle?: { key: string; is_active: boolean; toggled_at: string } }> {
    return this.request(`/admin/system-toggles/${encodeURIComponent(key)}`, {
      method: "PATCH",
      body: JSON.stringify({ isActive }),
    });
  }

  async createTenant(
    name: string,
    slug?: string,
  ): Promise<{ success: boolean; tenant?: { id: string; name: string; slug: string; status: string }; error?: string }> {
    return this.post<{ success: boolean; tenant?: { id: string; name: string; slug: string; status: string }; error?: string }>(
      "/admin/tenants",
      { name, ...(slug ? { slug } : {}) },
    );
  }

  async createCampaign(
    tenantId: string,
    payload: CreateCampaignPayload,
  ): Promise<CreateCampaignResponse> {
    return this.post<CreateCampaignResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/campaigns`,
      payload as unknown as Record<string, unknown>,
    );
  }

  async updateCampaignStatus(
    tenantId: string,
    campaignId: string,
    status: string,
  ): Promise<CampaignStatusUpdateResponse> {
    return this.request<CampaignStatusUpdateResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/campaigns/${encodeURIComponent(campaignId)}/status`,
      {
        method: "PATCH",
        body: JSON.stringify({ status }),
      },
    );
  }

  async resolveCampaignTenant(campaignKey: string): Promise<CampaignResolveResponse> {
    return this.get<CampaignResolveResponse>(
      `/campaigns/resolve?campaign_key=${encodeURIComponent(campaignKey)}`,
    );
  }

  async wizardScrapeAndGenerate(
    brief: WizardBriefFields,
  ): Promise<WizardScrapeResponse> {
    return this.post<WizardScrapeResponse>(
      "/campaign-wizard/scrape",
      brief as unknown as Record<string, unknown>,
    );
  }

  async wizardRegenerateFields(
    request: WizardRegenerateRequest,
  ): Promise<WizardRegenerateResponse> {
    return this.post<WizardRegenerateResponse>(
      "/campaign-wizard/regenerate",
      request as unknown as Record<string, unknown>,
    );
  }

  async resolveEmailTenant(email: string): Promise<EmailTenantResolveResponse> {
    return this.get<EmailTenantResolveResponse>(
      `/auth/resolve-tenant?email=${encodeURIComponent(email)}`,
    );
  }

  async removeMcpConnector(name: string): Promise<McpRemoveResponse> {
    return this.delete<McpRemoveResponse>(
      `/mcp-connector/${encodeURIComponent(name)}`,
    );
  }

  async manageConnector(
    action: "add" | "remove",
    connector: string,
  ): Promise<ConnectorManagerResponse> {
    return this.post<ConnectorManagerResponse>("/connector-manager", {
      action,
      connector,
    });
  }

  // ── Event Monitoring ─────────────────────────────────────────────────────────

  async ingestEvents(events: MonitoringEventInput[]): Promise<{ success: boolean; inserted: number }> {
    if (!this.hasAuthToken()) {
      return { success: true, inserted: 0 };
    }

    return this.post<{ success: boolean; inserted: number }>("/events", { events });
  }

  private buildEventQS(params: MonitoringQueryParams): URLSearchParams {
    const qs = new URLSearchParams();
    if (params.type)     qs.set("type", params.type);
    if (params.severity) qs.set("severity", params.severity);
    if (params.from)     qs.set("from", params.from);
    if (params.to)       qs.set("to", params.to);
    if (params.search)   qs.set("search", params.search);
    if (params.user_id)  qs.set("user_id", params.user_id);
    if (params.domain)   qs.set("domain", params.domain);
    if (params.page)     qs.set("page", String(params.page));
    if (params.limit)    qs.set("limit", String(params.limit));
    return qs;
  }

  async getEvents(params: MonitoringQueryParams = {}): Promise<MonitoringEventsResult> {
    const qs = this.buildEventQS(params);
    const query = qs.toString() ? `?${qs.toString()}` : "";
    return this.get<MonitoringEventsResult>(`/events${query}`);
  }

  async getEventsAggregate(params: MonitoringQueryParams & { aggregate: "hourly" | "daily" }): Promise<MonitoringAggregateResult> {
    const qs = this.buildEventQS(params);
    qs.set("aggregate", params.aggregate);
    return this.get<MonitoringAggregateResult>(`/events?${qs.toString()}`);
  }

  async getEventsMetrics(params: MonitoringQueryParams & { aggregate?: "hourly" | "daily" }): Promise<MonitoringMetricsResult> {
    const qs = this.buildEventQS(params);
    if (params.aggregate) qs.set("aggregate", params.aggregate);
    return this.get<MonitoringMetricsResult>(`/events/metrics?${qs.toString()}`)
  }

  // ---------------------------------------------------------------------------
  // Invitations
  // ---------------------------------------------------------------------------

  async sendInvitation(email: string, tenantSlug?: string): Promise<{ success: boolean; invitation?: InvitationRecord; error?: string }> {
    return this.post(`/admin/invitations`, { email, ...(tenantSlug ? { tenantSlug } : {}) });
  }

  async resetUserForReinvite(email: string): Promise<{ success: boolean; clerkDeleted?: boolean; error?: string }> {
    return this.request(`/admin/onboarding/reset`, {
      method: "POST",
      body: { email, deleteIdentity: true, deleteFromClerk: true },
    });
  }

  async listInvitations(tenantSlug?: string): Promise<{ success: boolean; invitations: InvitationRecord[]; tenantName: string | null; error?: string }> {
    const qs = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    try {
      return await this.get(`/admin/invitations${qs}`);
    } catch (error) {
      const requestError = error as ApiRequestError;
      const canTreatAsEmpty =
        (requestError.status === 400 && /no tenant resolved/i.test(requestError.message)) ||
        (requestError.status === 404 && /not found/i.test(requestError.message));

      if (canTreatAsEmpty) {
        return { success: true, invitations: [], tenantName: null };
      }

      throw error;
    }
  }

  async revokeInvitation(id: number): Promise<{ success: boolean; error?: string }> {
    return this.delete(`/admin/invitations/${id}`);
  }

  async resendInvitation(id: number): Promise<{ success: boolean; invitation?: InvitationRecord; error?: string }> {
    return this.post(`/admin/invitations/${id}/resend`, {});
  }
}

export interface InvitationRecord {
  id: number;
  email: string;
  role: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  invited_by_email: string | null;
}

export default new ApiService();
